import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { isEmpty, uniq } from 'lodash-es'
import Button from '@/components/content/Button'
import Text from '@/components/content/Text'
import { HStack, VStack } from '@musinsa/mwms-ds'
import { colorSystem, colors, zIndexSystem } from '@/styles/theme'
import { Utils } from '@/utils/Utils'

interface MultipleInputListProps {
  value: string
  isOpen: boolean
  isOnPanel?: boolean
  type?: 'string' | 'number'
  maxSize?: number
  hasNoLimit?: boolean
  style?: React.CSSProperties
  required?: boolean
  onApply?: (v: string[]) => void
  onClose: () => void
  onChangePanelValue?: (list: string[]) => void
}

export const MultipleInputList = forwardRef<HTMLDivElement, MultipleInputListProps>((props, ref) => {
  const {
    style,
    type = 'string',
    value,
    maxSize = 500,
    hasNoLimit = false,
    onApply,
    onClose,
    onChangePanelValue,
    isOpen,
    isOnPanel = false,
  } = props

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const [inputValue, setInputValue] = useState('')
  const isEmptyValue = isEmpty(inputValue)

  const parseInputToArray = useCallback((inputValue: string) => {
    return (inputValue || '')
      .trim()
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  }, [])

  const getUniqValues = useCallback(
    (inputValue: string) => {
      return uniq(parseInputToArray(inputValue))
    },
    [parseInputToArray],
  )

  const helperText = '입력 후 Enter키로 다중 입력이 가능합니다.'

  const onChangeAreaVal = useCallback(
    (v: string) => {
      const processedValue = !v ? '' : type === 'number' ? v.replace(/[^0-9,\n\r ]/g, '').replace(/(\..*)\./g, '$1') : v
      const parsedArray = parseInputToArray(processedValue)
      const uniqueValues = uniq(parsedArray)

      if (hasNoLimit) {
        setInputValue(processedValue)
        if (isOnPanel) {
          onChangePanelValue?.(parseInputToArray(processedValue))
        }
        return
      }

      // NOTE: 붙여넣기 여부 확인 (여러 줄이 한번에 추가되는 경우)
      const isPaste = Math.abs(v.split('\n').length - inputValue.split('\n').length) > 1

      // NOTE: maxSize 도달 시 개행 차단하여 추가하지 못하도록 함
      if (!isPaste && getUniqValues(inputValue).length >= maxSize) {
        const isNewLine = v.split('\n').length > inputValue.split('\n').length
        if (isNewLine) {
          return
        }
      }

      // NOTE: maxSize 초과 시 제한 처리
      const finalValue =
        uniqueValues.length > maxSize ? parsedArray.filter((line) => uniqueValues.slice(0, maxSize).includes(line)).join('\n') : processedValue

      setInputValue(finalValue)

      if (isOnPanel) {
        onChangePanelValue?.(parseInputToArray(finalValue))
      }
    },
    [type, isOnPanel, hasNoLimit, maxSize, inputValue, parseInputToArray, onChangePanelValue, getUniqValues],
  )

  const onReset = useCallback(() => {
    setInputValue('')
    if (isOnPanel) {
      onChangePanelValue?.([])
    }
  }, [isOnPanel, onChangePanelValue])

  const handleClose = useCallback(() => {
    onReset()
    onClose()
  }, [onReset, onClose])

  const onClickReset = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation()
    onReset()
  }

  const onClickApply = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation()
    onApply?.(getUniqValues(inputValue))

    handleClose()
  }

  useEffect(
    function onSetInitVal() {
      if (!value) {
        setInputValue('')
      }

      if (isOpen && !isOnPanel) textAreaRef.current?.focus()
      if (isOpen && value) {
        setInputValue(
          value
            ?.trim()
            .split(',')
            .filter((value) => value)
            .map((value) => value.trim())
            .slice(0, maxSize)
            .join('\n') ?? '',
        )
      }
    },
    [isOpen, isOnPanel, value, maxSize],
  )

  return (
    <ListWrapper ref={ref} $isOnPanel={isOnPanel} style={style}>
      <VStack gap={6} style={{ padding: isOnPanel ? '0' : '12px' }}>
        <StyledTextArea
          ref={textAreaRef}
          value={inputValue}
          placeholder="입력"
          onChange={(e) => onChangeAreaVal(e.currentTarget.value)}
          onFocus={(e) => e?.stopPropagation()}
          rows={4}
        />
        {!hasNoLimit &&
          (isEmptyValue ? (
            <Text kind="Body_12_regular" color={colors.gray500}>
              {helperText}
            </Text>
          ) : (
            <HStack align="center">
              <Text kind="Body_12_regular" color={colors.black}>
                {getUniqValues(inputValue).length}
              </Text>
              <Text kind="Body_12_regular" color={colors.gray500}>
                /{Utils.getCommaNum(maxSize)}
              </Text>
            </HStack>
          ))}
      </VStack>

      {!isOnPanel && (
        <HStack justify="flex-end" style={{ padding: '0 12px 12px' }} gap={8}>
          <Button onClick={onClickReset} kind="secondary" size="sm" fit>
            초기화
          </Button>
          <Button onClick={onClickApply} kind="primary_light" size="sm" fit>
            적용
          </Button>
        </HStack>
      )}
    </ListWrapper>
  )
})

const ListWrapper = styled.div<{ $isOnPanel: boolean }>`
  background: white;
  border-radius: 4px;

  ${({ $isOnPanel }) =>
    !$isOnPanel &&
    css`
      min-width: 280px;
      min-height: 160px;
      box-shadow: 0 10px 15px 8px rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(0, 0, 0, 0.1);
      z-index: ${zIndexSystem.modal - 1};
    `};

  ${({ $isOnPanel }) =>
    $isOnPanel &&
    css`
      width: 100%;
    `};
`

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 8px;
  border: 1px solid ${colorSystem.border.element};
  border-radius: 4px;
  outline: none;
  resize: vertical;
  font-size: 14px;
  line-height: 20px;
  color: ${colorSystem.text.common.primary};
  background-color: ${colorSystem.bg.input.enabled};

  &:hover,
  &:focus,
  &:active {
    border-color: ${colorSystem.border.element_active};
  }

  &::placeholder {
    color: ${colorSystem.text.common.tertiary};
  }
`
